import { useRef, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { Button, DateRangePopover, SectionCard } from "components/AtomsV2";
import { Icon } from "components/ShellV2";
import { communitiesApiSlice } from "state/communities/communitiesApiSlice";
import { useImportBusinessValidityMutation } from "state/businessValidity/businessValidityApiSlice";
import { openNotificationPopup } from "state/application/applicationSlice";
import { SIMULATOR_IMPORT_VERSION } from "utils/constants";
import { canonicalSnapshotToSimulator, lisbonToday } from "utils/businessValidity";

function ExportCommunity({ community }) {
  const dispatch = useDispatch();
  const { t } = useTranslation(["community"]);
  const [isExporting, setIsExporting] = useState(false);
  const [effectiveAt, setEffectiveAt] = useState(lisbonToday());
  const importInput = useRef(null);
  const [importHistory, { isLoading: isImporting }] = useImportBusinessValidityMutation();

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const data = await dispatch(
        communitiesApiSlice.endpoints.exportCommunity.initiate({
          communityId: community.id,
          mode: "snapshot",
          effectiveAt,
        })
      ).unwrap();

      const simulator = canonicalSnapshotToSimulator(data, SIMULATOR_IMPORT_VERSION);
      const json = JSON.stringify({ ...data, simulator }, null, 2);
      const blob = new Blob([json], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const filename = community.name
        ? `community_${community.name.replace(/\s+/g, "_").toLowerCase()}.json`
        : "community.json";
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } finally {
      setIsExporting(false);
    }
  };

  const handleImport = async (event) => {
    const [file] = event.target.files;
    event.target.value = "";
    if (!file) return;
    try {
      const payload = JSON.parse(await file.text());
      await importHistory({
        communityId: community.id,
        payload,
        legacyBaseEffectiveDate: payload.import_version === "1.0.14" ? effectiveAt : undefined,
      }).unwrap();
      dispatch(
        openNotificationPopup({
          notification: t("export.import_success", {
            defaultValue: "Community history imported successfully.",
          }),
          type: "success",
        })
      );
    } catch (error) {
      dispatch(
        openNotificationPopup({
          notification:
            error?.data?.detail ||
            t("export.import_error", { defaultValue: "Could not import this file." }),
          type: "error",
        })
      );
    }
  };

  return (
    <SectionCard
      id="export"
      icon="download"
      title={t("export.title")}
      action={
        <div className="flex gap-2">
          <input
            ref={importInput}
            type="file"
            accept="application/json,.json"
            className="hidden"
            onChange={handleImport}
          />
          <Button
            variant="ghost"
            size="sm"
            onClick={() => importInput.current?.click()}
            disabled={isImporting}
          >
            <Icon name={isImporting ? "hourglass_top" : "upload"} />
            {t("export.import_button", { defaultValue: "Import" })}
          </Button>
          <Button variant="ghost" size="sm" onClick={handleExport} disabled={isExporting}>
            <Icon name={isExporting ? "hourglass_top" : "download"} />
            {isExporting ? t("export.exporting") : t("export.button")}
          </Button>
        </div>
      }
    >
      <p className="text-[13px] text-ink-3 leading-[1.6] m-0 max-w-[560px]">
        {t("export.description")}
      </p>
      <div className="rec-field mt-3 max-w-[220px]">
        <span className="text-[12.5px] font-semibold text-ink-2 leading-none">
          {t("export.structure_date")}
        </span>
        <div className="self-start">
          <DateRangePopover
            mode="single"
            allowFuture
            ariaLabel={t("export.structure_date")}
            value={{ start: effectiveAt, end: effectiveAt }}
            onChange={({ start }) => setEffectiveAt(start)}
          />
        </div>
      </div>
    </SectionCard>
  );
}

ExportCommunity.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  community: PropTypes.object.isRequired,
};

export default ExportCommunity;
